import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useEditorAuth } from "@/context/EditorAuthContext";
import { Eye, FileText, Sparkles, Tag, TrendingUp, Calendar as CalIcon, ExternalLink, Share2, Radar, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PLATFORM_LABELS = {
  whatsapp: "WhatsApp", facebook: "Facebook", instagram: "Instagram", tiktok: "TikTok",
  linkedin: "LinkedIn", x: "X (Twitter)", telegram: "Telegram", messenger: "Messenger",
  email: "Email", copy: "Copy Link",
};
const PLATFORM_COLORS = {
  whatsapp: "#25D366", facebook: "#1877F2", instagram: "#E4405F", tiktok: "#0A1128",
  linkedin: "#0A66C2", x: "#000000", telegram: "#26A5E4", messenger: "#0084FF",
  email: "#688A4E", copy: "#6B7280",
};

export default function AnalyticsPanel() {
  const { authAxios } = useEditorAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seo, setSeo] = useState(null);
  const [reindexing, setReindexing] = useState(false);

  useEffect(() => {
    authAxios().get("/editor/analytics").then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
    authAxios().get("/editor/seo/status").then((r) => setSeo(r.data)).catch(() => {});
  }, []);

  const reindexAll = async () => {
    if (!window.confirm("Push every published article to IndexNow (Bing/Yandex/Naver/Seznam) and resubmit the sitemap to Google Search Console?")) return;
    setReindexing(true);
    try {
      const r = await authAxios().post("/editor/seo/reindex", {});
      toast.success(`Re-indexed ${r.data.count} URL(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-index failed");
    } finally {
      setReindexing(false);
    }
  };

  if (loading) return <div className="text-gray-500" data-testid="analytics-loading">Loading analytics…</div>;
  if (!stats) return <div className="text-gray-500">No data</div>;

  const maxMonth = Math.max(1, ...(stats.monthly_activity || []).map(m => m.count));
  const maxTag = Math.max(1, ...(stats.top_tags || []).map(t => t.count));
  const maxPlatform = Math.max(1, ...(stats.shares_by_platform || []).map(p => p.count));

  return (
    <div data-testid="analytics-panel" className="space-y-10">
      {/* Stat cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Eye} label="Total Views" value={stats.total_views} testId="stat-total-views" accent/>
        <StatCard icon={Share2} label="Total Shares" value={stats.total_shares || 0} testId="stat-total-shares" accent/>
        <StatCard icon={FileText} label="Published" value={stats.published} testId="stat-published"/>
        <StatCard icon={CalIcon} label="Scheduled" value={stats.scheduled} testId="stat-scheduled"/>
        <StatCard icon={Sparkles} label="Drafts" value={stats.drafts} testId="stat-drafts"/>
      </div>

      {/* Top articles */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline mb-1">Top Performing</div>
            <h3 className="font-serif text-2xl text-[#0A1128]">Most-read articles</h3>
          </div>
          <TrendingUp className="w-5 h-5 text-[#688A4E]"/>
        </div>
        {(!stats.top_articles || stats.top_articles.length === 0) ? (
          <div className="bg-white border border-gray-200 p-8 text-center text-gray-500 text-sm">No published articles yet.</div>
        ) : (
          <div className="bg-white border border-gray-200 divide-y divide-gray-100" data-testid="top-articles">
            {stats.top_articles.map((a, i) => (
              <div key={a.id} className="px-6 py-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className="font-serif text-2xl text-gray-300 w-8 shrink-0">{String(i + 1).padStart(2, "0")}</div>
                  <div className="min-w-0">
                    <Link to={`/blog/${a.slug}`} target="_blank" className="text-[#0A1128] hover:text-[#688A4E] truncate block font-medium" data-testid={`top-article-${a.slug}`}>{a.title}</Link>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(a.tags || []).slice(0, 3).map((t) => <span key={t} className="text-[10px] border border-gray-200 px-1.5 py-0.5 text-gray-500">{t}</span>)}
                    </div>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-2xl font-serif text-[#688A4E]">{a.view_count || 0}</div>
                  <div className="text-[10px] tracking-widest uppercase text-gray-400">views</div>
                </div>
                <Link to={`/blog/${a.slug}`} target="_blank" className="text-gray-400 hover:text-[#688A4E]"><ExternalLink className="w-4 h-4"/></Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Monthly activity + Top tags */}
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline mb-1">Social Reach</div>
              <h3 className="font-serif text-2xl text-[#0A1128]">Most-shared articles</h3>
            </div>
            <Share2 className="w-5 h-5 text-[#688A4E]"/>
          </div>
          <div className="bg-white border border-gray-200" data-testid="top-shared-articles">
            {(!stats.top_shared || stats.top_shared.length === 0) ? (
              <div className="p-8 text-center text-sm text-gray-500">No shares yet — readers will start sharing soon.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {stats.top_shared.map((a, i) => (
                  <div key={a.id} className="px-6 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="font-serif text-2xl text-gray-300 w-8 shrink-0">{String(i + 1).padStart(2, "0")}</div>
                      <Link to={`/blog/${a.slug}`} target="_blank" className="text-[#0A1128] hover:text-[#688A4E] truncate block font-medium" data-testid={`top-shared-${a.slug}`}>{a.title}</Link>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-2xl font-serif text-[#688A4E]">{a.share_count || 0}</div>
                      <div className="text-[10px] tracking-widest uppercase text-gray-400">shares</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline mb-1">Where they share</div>
              <h3 className="font-serif text-2xl text-[#0A1128]">Shares by platform</h3>
            </div>
          </div>
          <div className="bg-white border border-gray-200 p-6" data-testid="shares-by-platform">
            {(!stats.shares_by_platform || stats.shares_by_platform.length === 0) ? (
              <div className="text-center text-sm text-gray-500 py-8">Once readers start sharing, platform breakdown appears here.</div>
            ) : (
              <div className="space-y-3">
                {stats.shares_by_platform.map((p) => (
                  <div key={p.platform} className="flex items-center gap-3" data-testid={`platform-${p.platform}`}>
                    <div className="w-24 truncate text-xs text-[#0A1128]" title={p.platform}>{PLATFORM_LABELS[p.platform] || p.platform}</div>
                    <div className="flex-1 bg-[#F9F9F7] h-6 relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0" style={{ width: `${(p.count / maxPlatform) * 100}%`, background: PLATFORM_COLORS[p.platform] || "#0A1128" }}></div>
                    </div>
                    <div className="w-8 text-right text-sm font-medium text-[#0A1128]">{p.count}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Monthly activity + Top tags */}
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline mb-1">Publishing Cadence</div>
              <h3 className="font-serif text-2xl text-[#0A1128]">Articles per month</h3>
            </div>
          </div>
          <div className="bg-white border border-gray-200 p-6" data-testid="monthly-chart">
            {(stats.monthly_activity || []).length === 0 ? (
              <div className="text-gray-500 text-sm text-center py-8">No activity yet.</div>
            ) : (
              <div className="space-y-3">
                {stats.monthly_activity.map((m) => (
                  <div key={m.month} className="flex items-center gap-3">
                    <div className="w-20 text-xs text-gray-500 shrink-0">{m.month}</div>
                    <div className="flex-1 bg-[#F9F9F7] h-6 relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-[#688A4E]" style={{ width: `${(m.count / maxMonth) * 100}%` }}></div>
                    </div>
                    <div className="w-8 text-right text-sm font-medium text-[#0A1128]">{m.count}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline mb-1">Content Themes</div>
              <h3 className="font-serif text-2xl text-[#0A1128]">Top tags</h3>
            </div>
            <Tag className="w-5 h-5 text-[#688A4E]"/>
          </div>
          <div className="bg-white border border-gray-200 p-6" data-testid="top-tags">
            {(stats.top_tags || []).length === 0 ? (
              <div className="text-gray-500 text-sm text-center py-8">Publish tagged articles to see themes.</div>
            ) : (
              <div className="space-y-3">
                {stats.top_tags.map((t) => (
                  <div key={t.tag} className="flex items-center gap-3">
                    <div className="w-32 truncate text-xs text-[#0A1128]" title={t.tag}>{t.tag}</div>
                    <div className="flex-1 bg-[#F9F9F7] h-6 relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-[#0A1128]" style={{ width: `${(t.count / maxTag) * 100}%` }}></div>
                    </div>
                    <div className="w-6 text-right text-sm text-[#0A1128]">{t.count}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Keyword pipeline */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline mb-1">Keyword Pipeline</div>
            <h3 className="font-serif text-2xl text-[#0A1128]">Content backlog</h3>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4" data-testid="kw-pipeline">
          {[
            { label: "Total", value: stats.keywords.total, color: "text-[#0A1128]" },
            { label: "Unused", value: stats.keywords.unused, color: "text-gray-500" },
            { label: "Queued", value: stats.keywords.queued, color: "text-amber-600" },
            { label: "Used", value: stats.keywords.used, color: "text-[#688A4E]" },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-gray-200 p-6">
              <div className="overline mb-2 text-gray-400">{s.label}</div>
              <div className={`font-serif text-4xl ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* SEO artefacts */}
      <div className="bg-[#0A1128] text-white px-8 py-8" data-testid="seo-artefacts">
        <div className="overline text-[#688A4E] mb-3">SEO Artefacts</div>
        <div className="font-serif text-2xl mb-6">Discoverable by Google.</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`} target="_blank" rel="noreferrer" className="border border-white/20 hover:bg-white hover:text-[#0A1128] transition p-4 flex items-center justify-between" data-testid="link-sitemap">
            <div>
              <div className="text-xs tracking-widest uppercase opacity-70">Dynamic Sitemap</div>
              <div className="text-sm mt-1">/api/sitemap.xml</div>
            </div>
            <ExternalLink className="w-4 h-4"/>
          </a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/robots.txt`} target="_blank" rel="noreferrer" className="border border-white/20 hover:bg-white hover:text-[#0A1128] transition p-4 flex items-center justify-between" data-testid="link-robots">
            <div>
              <div className="text-xs tracking-widest uppercase opacity-70">robots.txt</div>
              <div className="text-sm mt-1">/api/robots.txt</div>
            </div>
            <ExternalLink className="w-4 h-4"/>
          </a>
        </div>
        <div className="text-xs text-[#A1A9BC] mt-4">Submit your sitemap URL in Google Search Console to accelerate indexing of every new blog article.</div>
      </div>

      {/* SEO indexing: IndexNow + Google Search Console */}
      {seo && (
        <div className="bg-white border border-gray-200 p-8" data-testid="seo-indexing-card">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Radar className="w-4 h-4 text-[#688A4E]"/>
                <div className="overline">Search-engine indexing</div>
              </div>
              <h3 className="font-serif text-2xl text-[#0A1128] mb-2">Auto-pinged on every publish.</h3>
              <p className="text-sm text-gray-600 max-w-xl">
                Every new article is pushed to IndexNow (Bing / Yandex / Naver / Seznam) the moment it goes live,
                and the sitemap is resubmitted to Google Search Console. Manual re-run below.
              </p>
            </div>
            <button
              onClick={reindexAll}
              disabled={reindexing}
              data-testid="seo-reindex-all"
              className="btn-primary"
            >
              {reindexing ? <><Loader2 className="w-4 h-4 animate-spin"/> Re-indexing…</> : <><Radar className="w-4 h-4"/> Re-index all published</>}
            </button>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-2 h-2 rounded-full ${seo.indexnow_key_set ? "bg-[#688A4E]" : "bg-gray-300"}`}></span>
                <div className="overline">IndexNow (Bing/Yandex/…)</div>
              </div>
              <div className="text-sm text-[#0A1128] font-medium mb-1">{seo.indexnow_key_set ? "Ready" : "Not configured"}</div>
              {seo.indexnow_key_url && (
                <a href={seo.indexnow_key_url} target="_blank" rel="noreferrer" className="text-xs text-[#688A4E] hover:underline break-all">
                  {seo.indexnow_key_url}
                </a>
              )}
            </div>
            <div className="border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-2 h-2 rounded-full ${seo.search_console_configured ? "bg-[#688A4E]" : "bg-amber-400"}`}></span>
                <div className="overline">Google Search Console</div>
              </div>
              <div className="text-sm text-[#0A1128] font-medium mb-1">
                {seo.search_console_configured ? "Ready" : "Awaiting service-account JSON"}
              </div>
              <div className="text-xs text-gray-500 break-all">{seo.search_console_site_url || "—"}</div>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-4">
            Site origin: <span className="font-mono">{seo.site_origin || "not set"}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId, accent }) {
  return (
    <div className={`p-6 border ${accent ? "border-[#688A4E] bg-[#F5F9EF]" : "border-gray-200 bg-white"}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="overline">{label}</div>
        <Icon className={`w-4 h-4 ${accent ? "text-[#688A4E]" : "text-gray-400"}`}/>
      </div>
      <div className="font-serif text-4xl text-[#0A1128]">{value ?? "—"}</div>
    </div>
  );
}
